// Deterministic forced-route re-preview helpers (R2-3b).
//
// Picking an alternative resource (or "Build fresh") from a Casper RunCard must
// re-preview DETERMINISTICALLY as that specific resource/execution-type (or as a
// brand-new workflow) — no LLM re-plan. These helpers build the exact
// `planCasperIntent` (POST /casper/plan) arguments for that request. Kept in a
// standalone module so the mapping is unit-testable without mounting the full
// CasperWorkspace component.

// Mirror the backend `_execution_type_for_entity`
// (casper/route_second_gate.py): agent→single_agent_run,
// workflow→existing_workflow_run, tool→tool_call, knowledge→knowledge_search.
export const mapResourceTypeToExecType = (type) => {
  switch (String(type || "").toLowerCase()) {
    case "agent":
      return "single_agent_run";
    case "workflow":
      return "existing_workflow_run";
    case "tool":
      return "tool_call";
    case "knowledge":
      return "knowledge_search";
    default:
      return undefined;
  }
};

// Build the /casper/plan args for a forced re-preview.
//   picked === { type: "build_fresh" }  → { force_new_workflow: true }
//   picked === { type, id, version? }   → { forced_resource, forced_execution_type }
// Returns null when `picked` is not a recognizable re-preview target so the
// caller can fall back to the legacy route-picker behavior.
export const buildRepreviewPlanArgs = ({ picked, prompt, sessionId }) => {
  if (!picked || typeof picked !== "object") return null;

  const args = { prompt };
  if (sessionId) args.session_id = sessionId;

  if (picked.type === "build_fresh") {
    args.force_new_workflow = true;
    return args;
  }

  const forcedExecutionType = mapResourceTypeToExecType(picked.type);
  // Require BOTH a recognizable type AND an id: malformed UI data must NOT
  // silently degrade to a normal LLM re-plan (it would forced_resource with a
  // null id and hydrate to nothing).
  if (!forcedExecutionType || !picked.id) return null;

  args.forced_resource = {
    type: picked.type,
    id: picked.id,
    version: picked.version,
  };
  args.forced_execution_type = forcedExecutionType;
  return args;
};
